import { Cushion, Grid, GridItem, MaxWidth, Rectangle, Typography } from '@pitchero/react-ui';
import { find, flatMap } from 'lodash';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import RosBottomBanner from '../adverts/ros-bottom-banner';
import SeoHead from '../seo';
import { Trans } from '../../lib/i18n';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import useBreakpoint from '../../lib/use-breakpoint';
import ActivityStreamMessage from '../activity-stream/message';
import HomepageMedia from '../homepage/media';
import MoreLink from '../homepage/more-link';
import NewsFilterBar from './filter-bar';
import NewsLoading from './loading';
import NoResults from './no-results';
import Sidebar from './sidebar';

const NUMBER_OF_ARTICLES_BEFORE_ADVERT = 5;
const NUMBER_OF_ARTICLES_BEFORE_LOAD_MORE = 11;

const News = ({
  category,
  club,
  isLoading,
  messages,
  month,
  onChangeCategoryFilter,
  onChangeMonthFilter,
  onChangeTeamFilter,
  sections,
  t,
  teamId,
}) => {
  let titleI18nKey = 'news:title_news';
  let headingI18nKey = 'news:heading_news';
  let metaDescriptionI18nKey = 'news:description_news';
  const translationValues = {};
  if (teamId) {
    const selectedTeam = find(
      flatMap(sections, (section) => section.teams),
      { id: teamId },
    );
    if (selectedTeam) {
      headingI18nKey += '_team';
      titleI18nKey += '_team';
      metaDescriptionI18nKey += '_team';
      translationValues.team = selectedTeam.name;
    }
  }

  if (category) {
    headingI18nKey += '_category';
    titleI18nKey += '_category';
    metaDescriptionI18nKey += '_category';
    translationValues.category = t(`${category}_activity_type_plural`);
  }

  if (month) {
    headingI18nKey += '_month';
    titleI18nKey += '_month';
    metaDescriptionI18nKey += '_month';
    translationValues.month = DateTime.fromFormat(`${month}-01`, 'yyyy-MM-dd').toFormat(
      'MMMM yyyy',
    );
  }

  const isMobile = useBreakpoint();
  const [showAllArticles, setShowAllArticles] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [videos, setVideos] = useState([]);
  const [nonMediaMessages, setNonMediaMessages] = useState([]);
  const mediaTypes = ['album', 'video'];
  const [openFilter, setOpenFilter] = useState(null);

  const onFilterClick = (filter) => {
    if (openFilter === filter) {
      setOpenFilter(null);
    } else {
      setOpenFilter(filter);
    }
  };

  useEffect(() => {
    setAlbums(messages.filter((message) => message.type === 'album'));
    setVideos(messages.filter((message) => message.type === 'video'));
    setNonMediaMessages(messages.filter((message) => !mediaTypes.includes(message.type)));
  }, [messages, isLoading]);

  const renderMessages = (messagesSlice) =>
    messagesSlice.map((message) => (
      <ActivityStreamMessage
        key={message.id}
        message={message}
        stacked="fullHeader"
        isV2
        imageProps={{
          alignment: 'top',
          width: '600',
          height: '336',
          style: { borderRadius: '10px' },
          loading: 'lazy',
        }}
      />
    ));

  return (
    <>
      <SeoHead
        club={club}
        route="news"
        routeParams={{ teamId, category, month }}
        titleTranslationKey={titleI18nKey}
        descriptionTranslationKey={metaDescriptionI18nKey}
        translationValues={translationValues}
      />
      <Rectangle fill="#fff" style={{ paddingBottom: '24px' }}>
        <NewsFilterBar
          category={category}
          club={club}
          messages={messages}
          month={month}
          onChangeCategoryFilter={onChangeCategoryFilter}
          onChangeMonthFilter={onChangeMonthFilter}
          onChangeTeamFilter={onChangeTeamFilter}
          sections={sections}
          t={t}
          title={t(headingI18nKey, translationValues)}
          teamId={teamId}
          onFilterClick={onFilterClick}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
        />
        <MaxWidth maxWidth={1366} center component="div">
          {nonMediaMessages.length > 0 ? (
            <>
              <Grid preset="newsListContainer">
                <div>
                  <Cushion
                    bottom="medium"
                    responsive={[
                      {
                        minWidth: 'fullHeader',
                        props: { top: 'medium' },
                      },
                    ]}
                    component="div"
                  >
                    <Rectangle
                      fill="primary"
                      component="div"
                      responsive={[{ minWidth: 'fullHeader', props: { radius: '10px' } }]}
                    >
                      <ActivityStreamMessage
                        message={nonMediaMessages[0]}
                        stacked="desk"
                        club={club}
                        isHero={isMobile}
                        isNewsHero={!isMobile}
                      />
                    </Rectangle>
                  </Cushion>
                  <Cushion
                    responsive={[{ maxWidth: 'fullHeader', props: { horizontal: 'small' } }]}
                  >
                    <Grid preset="newsList">
                      {renderMessages(nonMediaMessages.slice(1, NUMBER_OF_ARTICLES_BEFORE_ADVERT))}
                    </Grid>
                    <Cushion vertical="medium" component="div">
                      <RosBottomBanner />
                    </Cushion>
                  </Cushion>
                  {(albums.length > 0 || videos.length > 0) && (
                    <Cushion vertical="medium" component="div">
                      <HomepageMedia
                        albums={albums}
                        videos={videos}
                        club={club}
                        showMoreLink={false}
                        style={
                          !isMobile
                            ? {
                                borderRadius: '10px',
                                overflow: 'hidden',
                                padding: '0 10px',
                                boxSizing: 'border-box',
                              }
                            : {}
                        }
                      />
                    </Cushion>
                  )}
                  <Cushion
                    responsive={[{ maxWidth: 'fullHeader', props: { horizontal: 'small' } }]}
                  >
                    <Grid preset="newsList">
                      {renderMessages(
                        nonMediaMessages.slice(
                          NUMBER_OF_ARTICLES_BEFORE_ADVERT,
                          NUMBER_OF_ARTICLES_BEFORE_LOAD_MORE,
                        ),
                      )}
                      {showAllArticles &&
                        renderMessages(nonMediaMessages.slice(NUMBER_OF_ARTICLES_BEFORE_LOAD_MORE))}
                      {!showAllArticles &&
                        nonMediaMessages.length > NUMBER_OF_ARTICLES_BEFORE_LOAD_MORE && (
                          <GridItem
                            responsive={[
                              { minWidth: 'fullHeader', props: { columnStart: 1, columnEnd: 3 } },
                            ]}
                            component="div"
                          >
                            <MoreLink onClick={() => setShowAllArticles(true)}>
                              <Typography preset="title" size={16}>
                                <Trans i18nKey="common:load_more">Load More</Trans>
                              </Typography>
                            </MoreLink>
                          </GridItem>
                        )}
                      {showAllArticles && (
                        <GridItem
                          responsive={[
                            { minWidth: 'fullHeader', props: { columnStart: 1, columnEnd: 3 } },
                          ]}
                          component="div"
                          style={{ textAlign: 'center' }}
                        >
                          <a href="#" onClick={() => onFilterClick('month')}>
                            <Typography preset="subtitle--small" color="primary">
                              &raquo;Want more news? Use the filters to find more articles
                            </Typography>
                          </a>
                        </GridItem>
                      )}
                    </Grid>
                  </Cushion>
                </div>
                <div style={{ boxSizing: 'border-box', marginTop: '24px', paddingRight: '12px' }}>
                  <Sidebar club={club} />
                </div>
              </Grid>
            </>
          ) : (
            <>
              {isLoading ? (
                <NewsLoading isMobile={isMobile} />
              ) : (
                <>
                  <NoResults
                    category={category}
                    month={month}
                    teamId={teamId}
                    sections={sections}
                    t={t}
                  />
                  <RosBottomBanner />
                </>
              )}
            </>
          )}
        </MaxWidth>
      </Rectangle>
    </>
  );
};

News.defaultProps = {
  category: null,
  month: null,
  teamId: null,
};

News.propTypes = {
  category: PropTypes.string,
  club: CLUB_PROP_TYPES.isRequired,
  isLoading: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
    }),
  ).isRequired,
  month: PropTypes.string,
  onChangeCategoryFilter: PropTypes.func.isRequired,
  onChangeMonthFilter: PropTypes.func.isRequired,
  onChangeTeamFilter: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      teams: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  t: PropTypes.func.isRequired,
  teamId: PropTypes.number,
};

export default News;
